import axios, { AxiosError } from "axios";

/**
 * The one configured axios the whole app shares.
 *
 * Auth is a server-side Cookie/OIDC session — no token is ever handled in JavaScript — so the only
 * transport configuration needed is `withCredentials` plus a 401 handler that bounces to sign-in.
 * Tenant selection rides on the session's context claim, not a client-set header, which is why
 * switching organisation is a round trip through `/account/signin?select=true` rather than a
 * localStorage write.
 *
 * Export the module default as the transport for the generated NSwag clients too (see api/clients.ts)
 * so they inherit these interceptors instead of each spinning up a bare `axios.create()`.
 */

axios.defaults.withCredentials = true;

axios.interceptors.response.use(
    function (response) {
        // The identity server answers an expired session with its own HTML login page rather than a
        // 401, so a text/html body from that origin means the session is gone.
        const contentType = response.headers?.["content-type"]?.toString() ?? "";
        const responseUrl = response.request?.responseURL ?? "";
        // @ts-ignore -- window.identityServer is injected by Pages/Shared/_Layout.cshtml
        const identityServer = typeof window !== "undefined" ? window.identityServer : undefined;
        if (identityServer && contentType.indexOf("text/html") !== -1 && responseUrl.indexOf(identityServer) === 0) {
            window.location.pathname = "/Account/SignIn";
        }
        return response;
    },
    function (error: AxiosError) {
        if (error.isAxiosError && error.response == null) {
            return Promise.reject(error);
        }
        // axios puts the HTTP status on error.response.status; error.code holds strings like
        // "ERR_BAD_REQUEST", so checking error.code never matched and an expired session surfaced
        // as raw error text instead of a redirect.
        if (error.response?.status === 401) {
            const returnTo = window.location.pathname + window.location.search;
            window.location.href = "/Account/SignIn?redirectUrl=" + encodeURIComponent(returnTo);
        }
        return Promise.reject(error);
    }
);

/** Trim every string field on a request body in place. Non-string fields are left alone. */
export let TrimFields = <T extends Record<string, any>>(obj: T): T => {
    Object.keys(obj).forEach((k) => {
        if (typeof obj[k]?.trim === "function") {
            (obj as Record<string, any>)[k] = obj[k].trim();
        }
    });
    return obj;
};

/**
 * The RPC-style envelope, re-exported from its real home so existing imports keep working.
 * These previously declared `result` and `code`; ScadsysCore's DefaultResponse/TypedResponse
 * actually serialize `value`, `message` and a nested `errors.errors[]`, so anything reading
 * `.result` was silently reading undefined.
 */
export type { DefaultResponse, TypedResponse, ErrorCollection, ErrorItem } from "../api/errors";

export default axios;
